import * as React from "react";
import { css } from "@emotion/core";
import { MaterialProperty } from "../../../../../../smokepit/src/types/MaterialVolumes";
import { findMaterialInSurveys } from "./materialsVolumesChartUtils";

interface LineTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
  propertyLabel: string;
  allSiteSurveysData: any[];
  formatAlreadyConvertedPropertyValue: (
    volume: number,
    prop: MaterialProperty
  ) => string;
  selectedLayerLabel: string | null;
  brightMaterialColorMap: Map<number, string>;
  onDateHover?: (dateStr: string | null) => void;
}

export const LineTooltip: React.FC<LineTooltipProps> = ({
  active,
  payload,
  label,
  propertyLabel,
  allSiteSurveysData,
  formatAlreadyConvertedPropertyValue,
  selectedLayerLabel,
  brightMaterialColorMap,
  onDateHover,
}) => {
  React.useEffect(() => {
    if (active && label) {
      onDateHover?.(label);
    } else {
      onDateHover?.(null);
    }
  }, [active, label, onDateHover]);

  if (active && payload && payload.length) {
    const sortedPayload = [...payload].sort(
      (a: { value: number }, b: { value: number }) => b.value - a.value
    );

    return (
      <div
        css={css`
          background: var(--twilight);
          border: 1px solid var(--neutral-5);
          border-radius: 4px;
          padding: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        `}
      >
        <p style={{ margin: 0, fontWeight: 600, marginBottom: "4px" }}>
          {label}
        </p>
        <p
          style={{
            margin: "0 0 6px 0",
            fontSize: "0.85rem",
            color: "var(--neutral-3)",
          }}
        >
          {propertyLabel}
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: "4px 12px",
          }}
        >
          {sortedPayload.map(
            (
              entry: {
                name: string;
                value: number;
                color: string;
                dataKey: string;
              },
              index: number
            ) => {
              const materialIdMatch = entry.dataKey.match(/^material_(\d+)$/);
              const materialId = materialIdMatch
                ? parseInt(materialIdMatch[1], 10)
                : null;

              const material = materialId
                ? findMaterialInSurveys(
                    allSiteSurveysData,
                    selectedLayerLabel,
                    materialId
                  )
                : null;

              const prop = material?.properties?.find(
                (p: MaterialProperty) => p.property_label === propertyLabel
              );

              const materialName = material?.name || entry.name;

              const formattedValue = prop
                ? formatAlreadyConvertedPropertyValue(entry.value, prop)
                : entry.value.toLocaleString();

              return (
                <React.Fragment key={index}>
                  <span
                    css={css`
                      color: ${materialId
                        ? brightMaterialColorMap.get(materialId) || entry.color
                        : entry.color};
                    `}
                  >
                    ● {materialName}
                  </span>
                  <span
                    style={{
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {formattedValue}
                  </span>
                </React.Fragment>
              );
            }
          )}
        </div>
      </div>
    );
  }
  return null;
};
